import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { products } from '../../mocks/products';
import ProductGallery from './components/ProductGallery';
import ProductInfo from './components/ProductInfo';
import ProductReviews from './components/ProductReviews';
import RelatedProducts from './components/RelatedProducts';
import Footer from '../home/components/Footer';
import useScrollReveal from '../../hooks/useScrollReveal';

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const product = products.find((p) => p.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!product) return;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://indmobility.com';
    const productUrl = `${siteUrl}/product/${product.id}`;

    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      sku: product.id,
      brand: { '@type': 'Brand', name: 'IND Mobility' },
      image: product.images[0],
      offers: {
        '@type': 'Offer',
        price: product.price,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: productUrl,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviewCount,
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Chairs', item: siteUrl },
        { '@type': 'ListItem', position: 3, name: product.name, item: productUrl },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${product.name} | IND Mobility`,
      description: product.description,
      url: productUrl,
      isPartOf: { '@type': 'WebSite', name: 'IND Mobility', url: siteUrl },
    };

    const scriptTags = [productSchema, breadcrumbSchema, webPageSchema].map((schema) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      scriptTags.forEach((el) => {
        if (document.head.contains(el)) document.head.removeChild(el);
      });
    };
  }, [product]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  if (!product) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <h1 className="text-4xl font-black mb-4">PRODUCT NOT FOUND</h1>
        <p className="text-gray-400 mb-8">The chair you are looking for does not exist.</p>
        <Link to="/" className="bg-teal-500 text-black px-8 py-3 rounded-full font-bold hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap">
          BACK TO HOME
        </Link>
      </div>
    );
  }

  const navLinks = [
    { label: 'CHAIRS', href: '/' },
    { label: 'TECHNOLOGY', href: '/#tech' },
    { label: 'LIFESTYLE', href: '/#lifestyle' },
    { label: 'COMMUNITY', href: '/#community' },
  ];

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    if (href === '/') {
      navigate('/');
    } else if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href.replace('/', ''));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-black'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-12">
            <Link to="/" className="cursor-pointer">
              <img
                src="https://static.readdy.ai/image/c396747a877dd1d0cb2c362594545533/3399b712b281456e4d34bcc6c75925ba.png"
                alt="IND Mobility - Powering Your Independence"
                width="48"
                height="48"
                className="h-12 w-auto"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                  className="text-sm font-medium hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-sm font-medium hover:text-teal-400 transition-colors cursor-pointer whitespace-nowrap">SHOP</Link>
            <Link to="/contact" className="bg-teal-500 text-black px-6 py-2 rounded-full text-sm font-bold hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap">TEST DRIVE</Link>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative z-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <div className="flex flex-col items-center justify-center w-6 h-5 relative">
              <span className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 absolute ${mobileMenuOpen ? 'rotate-45 top-1/2 -translate-y-1/2' : 'top-0'}`}></span>
              <span className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 absolute top-1/2 -translate-y-1/2 ${mobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}></span>
              <span className={`block w-6 h-0.5 bg-white rounded transition-all duration-300 absolute ${mobileMenuOpen ? '-rotate-45 top-1/2 -translate-y-1/2' : 'bottom-0'}`}></span>
            </div>
          </button>
        </div>
      </nav>

      <div className={`fixed inset-0 z-40 bg-black/95 backdrop-blur-md transition-all duration-500 md:hidden ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className={`flex flex-col items-center justify-center h-full transition-transform duration-500 ${mobileMenuOpen ? 'translate-y-0' : '-translate-y-8'}`}>
          <div className="flex flex-col items-center space-y-8 mb-12">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="text-3xl font-black tracking-tight hover:text-teal-400 transition-all duration-300 cursor-pointer"
                style={{ transitionDelay: mobileMenuOpen ? `${index * 80}ms` : '0ms', opacity: mobileMenuOpen ? 1 : 0, transform: mobileMenuOpen ? 'translateY(0)' : 'translateY(20px)' }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 mb-6">
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-teal-400 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="hover:text-teal-400 transition-colors cursor-pointer">Chairs</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-white">{product.name}</span>
          </div>
        </div>

        <ProductHeroSection product={product} />
        <AnimatedSection animation="fade-up" delay={100}><ProductReviews reviews={product.reviews} rating={product.rating} reviewCount={product.reviewCount} /></AnimatedSection>
        <AnimatedSection animation="fade-up" delay={100}><RelatedProducts currentId={product.id} /></AnimatedSection>
      </main>

      <Footer />
    </div>
  );
}

function AnimatedSection({ children, animation = 'fade-up', delay = 0 }: { children: React.ReactNode; animation?: 'fade-up' | 'fade-left' | 'fade-right'; delay?: number }) {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  const baseStyles = 'transition-all duration-700 ease-out';
  const hiddenMap = {
    'fade-up': 'opacity-0 translate-y-12',
    'fade-left': 'opacity-0 -translate-x-12',
    'fade-right': 'opacity-0 translate-x-12',
  };
  const visibleStyles = 'opacity-100 translate-y-0 translate-x-0';

  return (
    <div
      ref={ref}
      className={`${baseStyles} ${isVisible ? visibleStyles : hiddenMap[animation]}`}
      style={{ transitionDelay: isVisible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
}

function ProductHeroSection({ product }: { product: typeof products[0] }) {
  const { ref: galleryRef, isVisible: galleryVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: infoRef, isVisible: infoVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="max-w-7xl mx-auto px-6 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div
          ref={galleryRef}
          className={`transition-all duration-700 ease-out ${galleryVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
        >
          <ProductGallery images={product.images} name={product.name} />
        </div>
        <div
          ref={infoRef}
          className={`transition-all duration-700 ease-out delay-150 ${infoVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
        >
          <ProductInfo
            name={product.name}
            tagline={product.tagline}
            category={product.category}
            price={product.price}
            originalPrice={product.originalPrice}
            description={product.description}
            colors={product.colors}
            variants={product.variants}
            specs={product.specs}
            rating={product.rating}
            reviewCount={product.reviewCount}
          />
        </div>
      </div>
    </section>
  );
}
